<script setup lang="ts">
import type { UiIconName } from "@/components/UiIcon.vue";
import type { UiTone } from "@/definitions/tone";
import { computed, onMounted, onUpdated, useTemplateRef } from "vue";

import ReasonScope from "@/components/ReasonScope.vue";
import UiIcon from "@/components/UiIcon.vue";

/**
 * 안내 패널(표준 ⑤ `.ui-callout[data-tone]`)
 * - tone 은 뜻으로 고른다: info(기본, 안내) · neutral(참고 · 조회 전용 · 빈 상태) · success(완료) · warn(주의 · 잠김) · danger(오류 · 위험).
 * - 옛 프로토타입 값(blue · amber · red · green)도 받아 같은 뜻으로 옮긴다. 프로토타입이 `.callout.amber` 를 남색으로 덮어 옛 값은
 *   모양이 뜻과 달랐다 — **새 코드는 뜻 이름만 쓴다**.
 * - 호출부가 `data-tone` 을 직접 넘기면 그 값이 이긴다(하위 호환). 새 코드는 `tone` 하나만 쓴다.
 * - 같은 자리 · 같은 tone 의 안내 여러 개는 `items` 로 한 상자에 묶는다. tone 이 다르면 `PageAlerts`.
 * - `role`: alert = 사용자 동작 뒤 새로 생긴 오류(즉시 낭독), status = 결과 알림, note = 늘 있는 참고. 페이지 로드 때부터 있는
 *   안내에는 alert 를 주지 않는다(첫 화면에서 낭독이 끼어든다).
 * - `actions` 슬롯은 오류 안내의 「다시 시도」 같은 동작 줄(본문 아래). 버튼은 `size="sm"`(36)으로 둔다. 잠긴 이유는
 *   `reasonPlacement="row"`(줄 아래 전폭 한 줄 — `PageAlerts` 항목 슬롯의 `.actions` 도 같다, 73차 후반 ds-common). 78차: 기본 `auto` 가 이 슬롯 안에서
 *   `row` 로 해석되므로 호출부가 값을 적지 않는다(`ReasonScope`).
 * - `icon`: warn · danger 안내가 색만으로 뜻을 전하지 않게 왼쪽에 표지를 둔다(UXE-06). `true` 면 tone 기본(warn · danger 경고,
 *   info · neutral 정보, success 체크, ai 반짝), 이름을 주면 그 아이콘. 기본은 없음(기존 모양 그대로).
 * - 73차 후반(ux-a C5): `items` 항목마다 동작이 필요하면(전체현황 연동 오류 「수정 화면으로 →」 · 구성원 안내 · 게이트) 슬롯
 *   `#itemActions="{ item, index }"` — 항목 오른쪽 끝, **버튼 하나**: `LoadingButton variant="accent" size="sm"`(표 행 이동과 같은 결)
 *   또는 이동 후보가 여럿이면 `MenuButton variant="ghost" size="sm"`(73차 후반 fe-jf2 — D0 게이트의 조직적합성 4단계).
 *   슬롯을 주면 목록은 점 없는 행 묶음이 되고, 좁은 폭에서 버튼이 글 아래 줄로 꺾인다. 안내 상자 안 **primary 는 두지 않는다** —
 *   자리 규칙 넷(`PageHero #actions` · `CardFoot` · `StickyActionBar` · 모달 바닥)에 없다. `#actions` · `#itemActions` 안 `.btn.primary` 와
 *   accent sm 이 아닌 항목 버튼은 DEV 경고(렌더는 그대로).
 * - 82차 a-C1: `icon` 과 `items` 를 함께 쓰면 목록 불릿을 그리지 않는다(아이콘 + 불릿 이중 표지) — 항목은 본문과 같은 시작선에서 줄만 나뉜다.
 * - 78차 `embedded`(ux-b C4 · UXB78-11 · 13): 접힘 · 하위 상자 안 Callout 이 상자를 또 두르지 않게 선 · 면 · 반경 없이 왼쪽 3px tone 표지선만. 83차 M13(Ec): 전역
 *   callout 값이 토큰(`--ui-callout-*`)으로 돌아와 중요도 선언이 없어졌으므로 이 규칙도 중요도 없이 이긴다(같은 점수의 `main.scss` 규칙보다 scoped `[data-v]` 가 더 구체적).
 * - 74차(UXV74-09): 같은 부모 안에서 안내 상자가 **연달아** 서면(앞 형제가 `.ui-callout`, `PageAlerts` 밖) DEV 경고 — 같은 tone 은
 *   `items` 한 상자, tone 이 다르면 `PageAlerts`. 그래도 남는 연속 상자는 main.scss 74차 이음새가 간격 8 · 같은 tone 맞닿는 반경 0 으로 묶는다.
 * - 92차 C92-19(JR92-10): ≤620 에서 `#actions` 버튼이 상자 폭보다 길면(「역량모델 확정(이 직무의 직무역량 포함) 완료하러 가기」 298px) 상자 밖으로 뚫고 나갔다 — 긴 라벨은 **버튼 안에서 줄을 바꾼다**
 *   (`max-width: 100%` · 높이는 글줄 수만큼, 짧은 라벨은 한 줄 36 그대로). 글자 줄바꿈 해제(`white-space`)는 전역 `main.scss` ≤760 `.actions > .btn { nowrap !important }` 에서 안내 상자 동작 줄을 뺀 ds-css 규칙이 맡는다.
 * - 모달 · 시트 안 「되돌리기」(74차 UXV74-05)는 토스트가 아니라 뺀 자리의 한 줄 — `tone="neutral" role="status" :items` 한 항목 +
 *   `#itemActions` accent sm 「되돌리기」(`toaster.ts` `toastUndo` 머리 주석).
 */
/** 옛 프로토타입 색 이름(사용 중단 예정) — 새 코드는 뜻 tone(info · warn · danger · success)만 쓴다 */
type LegacyCalloutTone = "blue" | "amber" | "red" | "green";

/** `items` 한 항목 */
export interface CalloutItem {
  title: string;
  text?: string;
}

const props = withDefaults(
  defineProps<{
    /** 굵은 제목 줄 */
    title?: string;
    /** 본문 텍스트 (기본 슬롯으로 대체 가능) */
    text?: string;
    /** 뜻 tone. 옛 색 이름은 하위 호환으로만 받는다 */
    tone?: UiTone | LegacyCalloutTone;
    /** 낭독 역할 */
    role?: "alert" | "status" | "note";
    /** 페이지 최상위 띠(`ui-band`, 카드급 가로 여백 · 반경) */
    band?: boolean;
    /** 같은 tone 안내 목록(제목 + 설명). 본문 아래 목록으로 그린다 */
    items?: CalloutItem[];
    /** 왼쪽 표지 아이콘. true = tone 기본 아이콘, 이름 = 그 아이콘 */
    icon?: boolean | UiIconName;
    /** 접힘 · 하위 상자 안에 둘 때 — 선 · 면 · 반경 없이 왼쪽 tone 표지선만(상자 안 상자 해소, 78차 ux-b C4) */
    embedded?: boolean;
  }>(),
  {
    title: undefined,
    text: undefined,
    tone: "info",
    role: undefined,
    band: false,
    items: undefined,
    icon: false,
    embedded: false,
  },
);

const slots = defineSlots<{
  /** 본문 영역 */
  default?: () => unknown;
  /** 본문 아래 동작 줄(「다시 시도」 등) */
  actions?: () => unknown;
  /** `items` 항목 오른쪽 끝 동작(accent sm 하나) */
  itemActions?: (props: { item: CalloutItem; index: number }) => unknown;
}>();

const root = useTemplateRef<HTMLElement>("root");
/** 이미 낸 경고 종류(같은 종류는 한 번만 — PageHero 상한 경고와 같은 방식) */
const warned = new Set<string>();

/** DEV: 안내 상자 안 버튼 자리 규칙 — 슬롯 내용은 호출부가 정해 prop 으로 알 수 없어 렌더 뒤 DOM 을 본다 */
function warnActions(): void {
  const el = root.value;
  if (!el) return;
  const found: [kind: string, message: string][] = [];
  if (el.querySelector(".actions .btn.primary:not(.ui-menu .btn)"))
    found.push([
      "primary",
      "#actions 안 primary — 안내 상자에는 ghost · quiet sm 만(primary 자리는 PageHero · CardFoot · StickyActionBar · 모달 바닥)",
    ]);
  const itemButtons = [
    ...el.querySelectorAll<HTMLElement>(".ui-callout-item-actions .btn"),
  ].filter((btn) => !btn.closest(".ui-menu"));
  // 허용: accent sm(LoadingButton) 또는 ghost sm MenuButton(이동 후보 여럿). primary · md 는 경고
  if (
    itemButtons.some(
      (btn) =>
        !btn.classList.contains("ui-sm") ||
        !(
          btn.classList.contains("ui-accent") ||
          (btn.classList.contains("ui-menu-button") &&
            !btn.classList.contains("primary"))
        ),
    )
  )
    found.push([
      "item-actions",
      '#itemActions 는 항목당 하나 — LoadingButton variant="accent" size="sm" 또는 MenuButton variant="ghost" size="sm"',
    ]);
  if (itemButtons.length > (props.items?.length ?? 0))
    found.push(["item-count", "#itemActions 안 버튼은 항목당 하나"]);
  // 74차 UXV74-09 ②: 한 자리에 안내 상자가 연달아 서면 무엇이 지금 막는지 읽히지 않는다(37쌍).
  const previous = el.previousElementSibling;
  if (
    previous?.classList.contains("ui-callout") &&
    !el.closest(".ui-page-alerts")
  )
    found.push([
      "stacked",
      "안내 상자가 연달아 있습니다 — 같은 tone 은 items 로 한 상자에, tone 이 다르면 PageAlerts 로 합치세요",
    ]);
  for (const [kind, message] of found) {
    if (warned.has(kind)) continue;
    warned.add(kind);
    console.warn(
      `[Callout] ${message} (title: ${props.title ?? props.text ?? "-"})`,
    );
  }
}

if (import.meta.env.DEV) {
  onMounted(warnActions);
  onUpdated(warnActions);
}

const LEGACY_TONES = new Map<string, UiTone>([
  ["blue", "info"],
  ["amber", "warn"],
  ["red", "danger"],
  ["green", "success"],
]);

const dataTone = computed<UiTone>(
  () => LEGACY_TONES.get(props.tone) ?? (props.tone as UiTone),
);

/** `icon: true` 일 때 tone 별 표지 */
const TONE_ICONS: Record<UiTone, UiIconName> = {
  info: "info",
  neutral: "info",
  success: "check",
  warn: "alert",
  danger: "alert",
  ai: "sparkle",
};

const iconName = computed<UiIconName | undefined>(() => {
  if (props.icon === false) return undefined;
  return props.icon === true ? TONE_ICONS[dataTone.value] : props.icon;
});
</script>

<template>
  <div
    ref="root"
    class="callout ui-callout"
    :class="{
      'ui-band': band,
      'has-icon': iconName,
      'is-embedded': embedded,
    }"
    :data-tone="dataTone"
    :role="role"
  >
    <!-- 아이콘이 있으면 [표지 | 글 묶음] 2열. 전역 `.ui-callout` 패딩이 !important 라 패딩 대신 글 묶음 상자를 둔다.
         제목은 `b` — 프로토타입 `.callout b` 의 검정 `!important` 가 걷혀(78차 ds-css) tone 글자색을 덮지 않는다(예전엔 `strong` 으로 비껴 갔다). -->
    <template v-if="iconName">
      <UiIcon class="ui-callout-icon" :name="iconName" :size="16" />
      <div class="ui-callout-body">
        <b v-if="title" class="ui-callout-title">{{ title }}</b>
        <slot>{{ text }}</slot>
        <ul
          v-if="items?.length"
          class="ui-callout-items is-iconed"
          role="list"
          :class="{ 'has-actions': !!slots.itemActions }"
        >
          <li v-for="(item, index) in items" :key="`${index}-${item.title}`">
            <span class="ui-callout-item-text">
              <strong>{{ item.title }}</strong>
              <template v-if="item.text"> — {{ item.text }}</template>
            </span>
            <span v-if="slots.itemActions" class="ui-callout-item-actions">
              <ReasonScope place="row"
                ><slot name="itemActions" :item="item" :index="index"
              /></ReasonScope>
            </span>
          </li>
        </ul>
        <div v-if="$slots.actions" class="actions">
          <ReasonScope place="row"><slot name="actions" /></ReasonScope>
        </div>
      </div>
    </template>
    <template v-else>
      <b v-if="title">{{ title }}</b>
      <slot>{{ text }}</slot>
      <ul
        v-if="items?.length"
        class="ui-callout-items"
        :class="{ 'has-actions': !!slots.itemActions }"
        :role="slots.itemActions ? 'list' : undefined"
      >
        <li v-for="(item, index) in items" :key="`${index}-${item.title}`">
          <span class="ui-callout-item-text">
            <strong>{{ item.title }}</strong>
            <template v-if="item.text"> — {{ item.text }}</template>
          </span>
          <span v-if="slots.itemActions" class="ui-callout-item-actions">
            <ReasonScope place="row"
              ><slot name="itemActions" :item="item" :index="index"
            /></ReasonScope>
          </span>
        </li>
      </ul>
      <div v-if="$slots.actions" class="actions">
        <ReasonScope place="row"><slot name="actions" /></ReasonScope>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* 안내 목록 — 본문 글자(13px · 색 상속) 그대로, 항목 사이 4px. 제목은 굵게만(상자 제목 `b` 의 블록 모양을 쓰지 않는다). */
.ui-callout-items {
  display: grid;
  gap: var(--ui-gap-2xs);
  margin: var(--ui-gap-2xs) 0 0;
  padding-left: 18px;
}

/* 82차 a-C1(UXA82-08 · 09): 표지 아이콘이 있는 안내는 목록 불릿을 두지 않는다 — 아이콘 + 불릿 이중 표지(항목이 하나여도 점이 붙었다). 글 시작선은 본문과 같다. */
.ui-callout-items.is-iconed {
  padding-left: 0;
  list-style: none;
}

.ui-callout > b + .ui-callout-items,
.ui-callout-title + .ui-callout-items {
  margin-top: 0;
}

/* 표지 아이콘 — 제목(14 · 줄 높이 1.45 = 20px) 첫 줄 가운데. 색은 tone 글자색(currentColor). */
.ui-callout.has-icon {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  align-items: start;
  gap: var(--ui-gap-sm);
}

.ui-callout-icon {
  margin-top: 2px;
}

.ui-callout-body {
  min-width: 0;
}

/*
 * 82차 sys-C8(UXS82-10): 표지 아이콘 안내의 글 묶음은 줄 길이 상한(`--ui-measure` 72ch). 상자(.ui-callout)는 폭을 그대로 쓴다. 아이콘 없는 안내는 상자 직계 글이라 상한을 걸 자리가
 * 없다(호출부 `.ui-measure`). 항목마다 오른쪽 끝 동작(`#itemActions`)이 있는 안내는 버튼이 상자 오른쪽 끝에 서야 하므로 상한을 두지 않는다.
 */
.ui-callout-body:not(:has(.ui-callout-items.has-actions)) {
  max-width: min(100%, var(--ui-measure, 72ch));
}

.ui-callout.is-embedded {
  padding: 2px 0 2px 12px;
  border: 0;
  border-left: 3px solid var(--ui-callout-mark);
  border-radius: 0;
  background: transparent;
}

/* 글 묶음 안 제목 · 동작 줄 — 전역 `.ui-callout > b` · `.ui-callout > .actions` 와 같은 값(직계가 아니라 걸리지 않는다). */
.ui-callout-body > .ui-callout-title {
  display: block;
  margin: 0 0 4px;
  color: inherit;
  font-size: var(--ui-fs-md);
  font-weight: 800;
  line-height: 1.45;
}

.ui-callout-body > .actions {
  margin-top: 10px;
}

/*
 * 92차 C92-19: 긴 라벨 동작 버튼이 상자 폭을 넘지 않고 버튼 안에서 줄을 바꾼다. 높이는 글줄 수만큼(고정 높이 해제 — 최소 36 은 전역 `--ui-h-btn-sm` 중요도 선언이 지킨다),
 * 줄바꿈 해제(`white-space`)는 전역 ≤760 `.actions > .btn { nowrap !important }` 규칙이 안내 상자 동작 줄을 빼야 풀린다(ds-css 요청 — 이 규칙은 그 규칙이 풀린 뒤 효과가 난다).
 * 한글 단어 중간에서 꺾이지 않게 `keep-all`, 한 단어가 폭보다 길 때만 `anywhere`.
 */
@media (max-width: 620px) {
  .ui-callout .actions > :deep(.btn) {
    max-width: 100%;
    height: auto;
    word-break: keep-all;
    overflow-wrap: anywhere;
  }
}

.ui-callout-items > li {
  word-break: keep-all;
  overflow-wrap: break-word;
}

.ui-callout-items strong {
  font-weight: 800;
}

/*
 * 항목별 동작(73차 후반 C5) — 점 목록 대신 [글 · 버튼] 행 묶음. 글은 남는 폭, 버튼은 오른쪽 끝(accent sm 36). 글이 24ch 를 못 채우는
 * 좁은 폭에서는 버튼이 글 아래 줄로 꺾인다(≤390 안내 상자 안 2열 격자 금지). 행 사이 6 은 버튼 높이 때문에 목록 4 보다 넓다.
 */
.ui-callout-items.has-actions {
  gap: 6px;
  padding-left: 0;
  list-style: none;
}

.ui-callout-items.has-actions > li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--ui-gap-xs) var(--ui-gap-sm);
}

.ui-callout-item-text {
  flex: 1 1 24ch;
  min-width: 0;
}

.ui-callout-item-actions {
  display: inline-flex;
  flex: none;
  margin-left: auto;
}
</style>
